import React, { useContext } from 'react';
import {
  Appbar, Switch, Title, Paragraph, Divider,
} from 'react-native-paper';
import { useQuery, useMutation } from '@apollo/client';
import {
  NativeBaseProvider, ScrollView, Box, VStack, HStack,
} from 'native-base';
import { useToast } from 'react-native-toast-notifications';
import { MyNotificationSetting } from '../../gql/notification-setting/query';
import { UpdateMyNotificationSetting } from '../../gql/notification-setting/mutation';
import { AuthContext } from '../../context/AuthContext';
import LoadingIndicator from '../../components/commons/LoadingIndicator';

const notificationSettingsConfig = [
  {
    key: 'eventRegistrationOpen',
    label: 'Ouverture des inscriptions aux événements',
  },
  {
    key: 'eventRegistrationClosingReminder',
    label: 'Rappel de fermeture des inscriptions',
  },
  {
    key: 'eventRegistrationClosed',
    label: 'Fermeture des inscriptions aux événements',
  },
  {
    key: 'eventUpcomingReminder',
    label: "Rappel d'événement à venir",
  },
  {
    key: 'newAnnouncement',
    label: 'Nouvelles annonces',
  },
  {
    key: 'weightUpdateReminder',
    label: 'Rappel de mise à jour du poids',
  },
];

const pushKeys = notificationSettingsConfig.map((c) => c.key);
const emailKeys = notificationSettingsConfig.map((c) => `${c.key}Email`);

function ManageNotificationSettings() {
  const toast = useToast();
  const { authState } = useContext(AuthContext);
  const { data, loading, error } = useQuery(MyNotificationSetting, {
    skip: !authState.authenticated,
  });
  const [updateSettings] = useMutation(UpdateMyNotificationSetting, {
    refetchQueries: [{ query: MyNotificationSetting }],
  });

  const handleToggle = async (key, value) => {
    try {
      await updateSettings({ variables: { [key]: value } });
      toast.show('Paramètres mis à jour', { type: 'success' });
    } catch (e) {
      console.error(e);
      toast.show('Erreur lors de la mise à jour', { type: 'danger' });
    }
  };

  const handleToggleAll = async (type, value) => {
    const variables = {};
    const keys = type === 'push' ? pushKeys : emailKeys;
    keys.forEach((key) => {
      variables[key] = value;
    });
    try {
      await updateSettings({ variables });
      toast.show(`Toutes les notifications ${type} ont été mises à jour`, { type: 'success' });
    } catch (e) {
      console.error(e);
      toast.show(`Erreur lors de la mise à jour des notifications ${type}`, { type: 'danger' });
    }
  };

  if (error) {
    toast.show('Erreur de chargement des paramètres', { type: 'danger' });
  }

  const settings = data?.myNotificationSetting;

  const allPushEnabled = settings ? pushKeys.every((key) => settings[key]) : false;
  const allEmailEnabled = settings ? emailKeys.every((key) => settings[key]) : false;

  function NotificationSection({
    title, description, allEnabled, onToggleAll, config, type,
  }) {
    return (
      <Box bg="white" p={4} borderRadius="md" shadow={2}>
        <HStack justifyContent="space-between" alignItems="center" mb={2}>
          <Title>{title}</Title>
          <Switch
            value={allEnabled}
            onValueChange={onToggleAll}
          />
        </HStack>
        <Paragraph fontSize="sm" color="gray.500" mb={3}>
          {description}
        </Paragraph>
        <Divider />
        <VStack space={2} mt={3}>
          {config.map((item) => {
            const settingKey = type === 'push' ? item.key : `${item.key}Email`;
            return (
              <VStack key={settingKey} space={1} alignItems="flex-start">
                <HStack justifyContent="space-between" alignItems="center" w="100%">
                  <Paragraph flex={1} numberOfLines={1} ellipsizeMode="tail">{item.label}</Paragraph>
                  <Switch
                    value={settings[settingKey]}
                    onValueChange={(value) => handleToggle(settingKey, value)}
                  />
                </HStack>
              </VStack>
            );
          })}
        </VStack>
      </Box>
    );
  }

  return (
    <Box flex={1} safeArea>
      {loading && <LoadingIndicator text="Chargement des préférences..." />}
      <Appbar.Header>
        <Appbar.Content title="Configurer les notifications" />
      </Appbar.Header>
      <ScrollView flex={1}>
        {settings && (
          <VStack space={4} p={4}>
            <NotificationSection
              title="Notifications Push"
              description="Activez ou désactivez toutes les notifications push."
              allEnabled={allPushEnabled}
              onToggleAll={(value) => handleToggleAll('push', value)}
              config={notificationSettingsConfig}
              type="push"
            />
            <NotificationSection
              title="Notifications Email"
              description="Activez ou désactivez toutes les notifications par email."
              allEnabled={allEmailEnabled}
              onToggleAll={(value) => handleToggleAll('email', value)}
              config={notificationSettingsConfig}
              type="email"
            />
          </VStack>
        )}
      </ScrollView>
    </Box>
  );
}

export default function ManageNotificationSettingsScreen() {
  return (
    <NativeBaseProvider>
      <ManageNotificationSettings />
    </NativeBaseProvider>
  );
}
